import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../Header/DashboardHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faCalendarAlt, faHeading, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CreatBlogs.css';

function CreatBlogs() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Check if admin is logged in
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('admin-login');
    if (!isLoggedIn) {
      // Redirect to home page if not logged in
      navigate('/');
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    date: getCurrentDate(),
    content: ''
  });
  
  // Preview state
  const [imagePreview, setImagePreview] = useState(null);
  
  // Get current date in YYYY-MM-DD format
  function getCurrentDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle content change from Quill editor
  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content: content
    });
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // Remove selected image
  const removeImage = () => {
    setFormData({
      ...formData,
      image: null
    });
    setImagePreview(null);
    fileInputRef.current.value = '';
  };

  // Quill editor modules and formats
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };
  
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image', 'video'
  ];

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Create FormData object for file upload
    const blogData = new FormData();
    blogData.append('title', formData.title);
    blogData.append('date', formData.date);
    blogData.append('content', formData.content);
    if (formData.image) {
      blogData.append('image', formData.image);
    }
    
    // Generate excerpt from content (first 150 characters without HTML tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.content;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const excerpt = textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '');
    blogData.append('excerpt', excerpt);
    
    // Send data to backend
    fetch('https://b.centrexmarineservices.com/Api/Creat.php', {
      method: 'POST',
      body: blogData,
    })
    .then(response => {
      console.log('Response status:', response.status);
      // Log the raw response for debugging
      return response.text().then(text => {
        try {
          // Try to parse as JSON
          const data = JSON.parse(text);
          return { ok: response.ok, data };
        } catch (e) {
          // If not valid JSON, return the raw text for debugging
          console.error('Invalid JSON response:', text);
          return { ok: false, text };
        }
      });
    })
    .then(result => {
      if (!result.ok) {
        if (result.text) {
          throw new Error(`Server returned: ${result.text}`);
        } else {
          throw new Error(`HTTP error! Status: ${result.data.message || 'Unknown error'}`);
        }
      }
      
      console.log('Success:', result.data);
      // Show success toast
      toast.success('Blog post created successfully!');
      
      // Reset form after submission
      setFormData({
        title: '',
        image: null,
        date: getCurrentDate(),
        content: ''
      });
      setImagePreview(null);
      
      // Redirect to view blogs after a short delay
      setTimeout(() => {
        navigate('/dashboard/view-blogs');
      }, 2000);
    })
    .catch((error) => {
      console.error('Error details:', error);
      // Show error toast
      toast.error('Failed to create blog post. Please try again.');
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  };

  // Handle cancel button click
  const handleCancel = () => {
    // Prevent navigation if already submitting
    if (!isSubmitting) {
      navigate('/dashboard/view-blogs');
    }
  };

  // Only render content if user is authorized
  if (!isAuthorized) {
    return null; // Return nothing while checking authorization or redirecting
  }

  return (
    <div className="create-blogs-page">
      <DashboardHeader />
      
      <div className="create-blogs-container">
        <div className="page-header">
          <h1>Create New Blog Post</h1>
          <p>Fill in the details below to create a new blog post</p>
        </div>
        
        <form onSubmit={handleSubmit} className="create-blogs-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">
                <FontAwesomeIcon icon={faHeading} className="form-icon" />
                Blog Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter blog title"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="date">
                <FontAwesomeIcon icon={faCalendarAlt} className="form-icon" />
                Publication Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="image">
              <FontAwesomeIcon icon={faImage} className="form-icon" />
              Blog Image
            </label>
            <div className="image-upload-container">
              <input
                type="file"
                id="image"
                name="image"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden-file-input"
                disabled={isSubmitting}
              />
              
              {!imagePreview ? (
                <div 
                  className="image-upload-placeholder" 
                  onClick={!isSubmitting ? triggerFileInput : undefined}
                  style={isSubmitting ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                >
                  <FontAwesomeIcon icon={faImage} className="upload-icon" />
                  <p>Click to select an image</p>
                  <span>Recommended size: 1200 x 800 pixels</span>
                </div>
              ) : (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={removeImage}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label>Blog Content</label>
            <div className="quill-editor-container">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                placeholder="Write your blog content here..."
                readOnly={isSubmitting}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faTimes} className="btn-icon" />
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="btn-icon" />
              {isSubmitting ? 'Creating...' : 'Create Blog Post'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Toast container for notifications */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default CreatBlogs;
